import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

const ContractAnalysisDetail = () => {
    const { id } = useParams();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        fetchAnalysis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchAnalysis = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`/api/contracts/analyses/${id}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setAnalysis(data);
        } catch (err) {
            console.error('Error fetching analysis:', err);
        } finally {
            setLoading(false);
        }
    };

    const getRiskColor = (level) => {
        switch (level) {
            case 'NONE': return 'bg-green-100 text-green-800';
            case 'LOW': return 'bg-blue-100 text-blue-800';
            case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
            case 'HIGH': return 'bg-orange-100 text-orange-800';
            case 'CRITICAL': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'INFO': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'WARNING': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!analysis) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-500">Analysis not found</p>
                <Link to="/contracts" className="text-blue-600 hover:underline">Back to Dashboard</Link>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <nav className="text-sm text-gray-500 mb-2">
                    <Link to="/contracts" className="hover:text-blue-600">Contract Analyzer</Link>
                    <span className="mx-2">›</span>
                    <span>Analysis Details</span>
                </nav>
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{analysis.document_title}</h1>
                        <div className="flex items-center gap-3 mt-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${getRiskColor(analysis.risk_category)}`}>
                                {analysis.risk_category || 'Unknown'} Risk
                            </span>
                            <span className="text-sm text-gray-500">
                                Analyzed {new Date(analysis.created_at).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => {/* Re-analyze */ }}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        >
                            Re-analyze
                        </button>
                        <button
                            onClick={() => {/* Export */ }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Export Report
                        </button>
                    </div>
                </div>
            </div>

            {/* Risk Score Banner */}
            {analysis.overall_risk_score !== null && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="md:col-span-2">
                            <h3 className="text-sm font-medium text-gray-500 mb-2">Risk Score</h3>
                            <div className="flex items-center gap-4">
                                <div className="text-4xl font-bold text-gray-900">{analysis.overall_risk_score}</div>
                                <div className="flex-1">
                                    <div className="w-full bg-gray-200 rounded-full h-4">
                                        <div
                                            className={`h-4 rounded-full ${analysis.overall_risk_score > 60 ? 'bg-red-500' :
                                                analysis.overall_risk_score > 40 ? 'bg-yellow-500' :
                                                    'bg-green-500'
                                                }`}
                                            style={{ width: `${analysis.overall_risk_score}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-500 mb-2">Clauses Found</h3>
                            <p className="text-3xl font-bold text-gray-900">{analysis.clauses?.length || 0}</p>
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-gray-500 mb-2">Risks Identified</h3>
                            <p className="text-3xl font-bold text-gray-900">{analysis.risk_flags?.length || 0}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100">
                    <nav className="flex space-x-8 px-6" aria-label="Tabs">
                        {['overview', 'clauses', 'risks', 'recommendations'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-6">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {analysis.summary && (
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Summary</h3>
                                    <p className="text-gray-700 whitespace-pre-line">{analysis.summary}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3">Contract Details</h3>
                                    <dl className="space-y-2">
                                        <div className="flex justify-between py-2 border-b border-gray-100">
                                            <dt className="text-gray-500">Type</dt>
                                            <dd className="text-gray-900">{analysis.contract_type || 'Unknown'}</dd>
                                        </div>
                                        {analysis.governing_law && (
                                            <div className="flex justify-between py-2 border-b border-gray-100">
                                                <dt className="text-gray-500">Governing Law</dt>
                                                <dd className="text-gray-900">{analysis.governing_law}</dd>
                                            </div>
                                        )}
                                        {analysis.effective_date && (
                                            <div className="flex justify-between py-2 border-b border-gray-100">
                                                <dt className="text-gray-500">Effective Date</dt>
                                                <dd className="text-gray-900">{analysis.effective_date}</dd>
                                            </div>
                                        )}
                                        {analysis.expiration_date && (
                                            <div className="flex justify-between py-2 border-b border-gray-100">
                                                <dt className="text-gray-500">Expiration Date</dt>
                                                <dd className="text-gray-900">{analysis.expiration_date}</dd>
                                            </div>
                                        )}
                                        {analysis.total_value && (
                                            <div className="flex justify-between py-2 border-b border-gray-100">
                                                <dt className="text-gray-500">Total Value</dt>
                                                <dd className="text-gray-900">{analysis.currency} {analysis.total_value.toLocaleString()}</dd>
                                            </div>
                                        )}
                                    </dl>
                                </div>

                                {analysis.parties?.length > 0 && (
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-3">Parties</h3>
                                        <div className="space-y-3">
                                            {analysis.parties.map((party, index) => (
                                                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                                                    <p className="font-medium text-gray-900">{party.name}</p>
                                                    <p className="text-sm text-gray-500">{party.role}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {analysis.key_terms?.length > 0 && (
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3">Key Terms</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {analysis.key_terms.map((term, index) => (
                                            <div key={index} className="p-3 border border-gray-200 rounded-lg">
                                                <p className="font-medium text-gray-900">{term.term}</p>
                                                <p className="text-sm text-gray-600">{term.definition}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Clauses Tab */}
                    {activeTab === 'clauses' && (
                        <div className="space-y-4">
                            {analysis.clauses?.length > 0 ? (
                                analysis.clauses.map((clause, index) => (
                                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                                        <div className="p-4 bg-gray-50 flex items-center justify-between">
                                            <div>
                                                <span className="text-xs text-gray-500 uppercase">{clause.clause_type_display}</span>
                                                <h4 className="font-medium text-gray-900">{clause.title || 'Untitled Clause'}</h4>
                                            </div>
                                            <span className={`px-2 py-1 text-xs rounded-full ${getRiskColor(clause.risk_level)}`}>
                                                {clause.risk_level_display}
                                            </span>
                                        </div>
                                        <div className="p-4">
                                            {clause.summary && (
                                                <p className="text-gray-600 mb-3">{clause.summary}</p>
                                            )}
                                            <details className="text-sm">
                                                <summary className="cursor-pointer text-blue-600 hover:text-blue-700">
                                                    View full text
                                                </summary>
                                                <p className="mt-2 p-3 bg-gray-50 rounded text-gray-700 whitespace-pre-wrap">
                                                    {clause.text}
                                                </p>
                                            </details>
                                            {clause.suggested_revision && (
                                                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                                    <p className="text-sm font-medium text-yellow-800">Suggested Revision:</p>
                                                    <p className="text-sm text-yellow-700">{clause.suggested_revision}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-gray-500 py-8">No clauses extracted</p>
                            )}
                        </div>
                    )}

                    {/* Risks Tab */}
                    {activeTab === 'risks' && (
                        <div className="space-y-4">
                            {analysis.risk_flags?.length > 0 ? (
                                analysis.risk_flags.map((risk, index) => (
                                    <div key={index} className={`border rounded-lg overflow-hidden ${getSeverityColor(risk.severity)}`}>
                                        <div className="p-4">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <span className={`text-xs font-medium uppercase ${risk.severity === 'CRITICAL' ? 'text-red-600' :
                                                        risk.severity === 'HIGH' ? 'text-orange-600' :
                                                            risk.severity === 'WARNING' ? 'text-yellow-600' :
                                                                'text-blue-600'
                                                        }`}>{risk.severity_display} • {risk.category_display}</span>
                                                    <h4 className="font-medium text-gray-900 mt-1">{risk.title}</h4>
                                                </div>
                                                {risk.is_resolved && (
                                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                                        Resolved
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-gray-700 mt-2">{risk.description}</p>
                                            {risk.text_excerpt && (
                                                <blockquote className="mt-3 pl-3 border-l-4 border-gray-300 text-sm text-gray-600 italic">
                                                    "{risk.text_excerpt}"
                                                </blockquote>
                                            )}
                                            {risk.recommendation && (
                                                <div className="mt-3 p-3 bg-white rounded border">
                                                    <p className="text-sm font-medium text-gray-900">Recommendation:</p>
                                                    <p className="text-sm text-gray-700">{risk.recommendation}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-gray-500 py-8">No risks identified</p>
                            )}
                        </div>
                    )}

                    {/* Recommendations Tab */}
                    {activeTab === 'recommendations' && (
                        <div className="space-y-6">
                            {analysis.recommendations?.length > 0 && (
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3">Recommendations</h3>
                                    <ul className="space-y-2">
                                        {analysis.recommendations.map((rec, index) => (
                                            <li key={index} className="flex items-start p-3 bg-blue-50 rounded-lg">
                                                <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span className="text-blue-800">{rec}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {analysis.suggested_revisions?.length > 0 && (
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3">Suggested Revisions</h3>
                                    <div className="space-y-4">
                                        {analysis.suggested_revisions.map((rev, index) => (
                                            <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="p-3 bg-red-50 border-b border-gray-200">
                                                    <p className="text-sm font-medium text-red-800">Original:</p>
                                                    <p className="text-red-700">{rev.original}</p>
                                                </div>
                                                <div className="p-3 bg-green-50">
                                                    <p className="text-sm font-medium text-green-800">Suggested:</p>
                                                    <p className="text-green-700">{rev.revised}</p>
                                                </div>
                                                {rev.reason && (
                                                    <div className="p-3 bg-gray-50 border-t border-gray-200">
                                                        <p className="text-sm text-gray-600"><strong>Reason:</strong> {rev.reason}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(!analysis.recommendations?.length && !analysis.suggested_revisions?.length) && (
                                <p className="text-center text-gray-500 py-8">No recommendations available</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContractAnalysisDetail;
